import React, { useState } from "react"
import { Button, Checkbox, Drawer, Form, Input, Spin } from "antd"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { useGet } from "utils/useFetch"
import type { IEmployeeAssignmentRes } from "../typings"
import { useParams } from "react-router"

// ── types ────────────────────────────────────────────────────────────
interface StaffMember {
  id: string
  name: string
  initials: string
}

interface Department {
  id: string
  name: string
  staff: StaffMember[]
}

interface StaffSelectionDrawerProps {
  open: boolean
  onClose: () => void
  /** Selected employees (id + name). Name is used for display in tables. */
  onSave: (selected: { id: string; name: string }[]) => void
  /** When true, Save button shows loading and is disabled (parent closes drawer when done) */
  saveLoading?: boolean
  /** Pre-selected employee IDs for the current slot/day */
  preSelectedIds?: string[]
}

const getInitials = (name: string): string => {
  const parts = name
    .split(" ")
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length === 0) return ""
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return parts[0].charAt(0).toUpperCase() + parts[1].charAt(0).toUpperCase()
}

const StaffSelectionDrawer: React.FC<
  StaffSelectionDrawerProps & { workplaceId: number }
> = ({
  open,
  onClose,
  onSave,
  saveLoading = false,
  preSelectedIds = [],
  workplaceId,
}) => {
  const [form] = Form.useForm()
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [search, setSearch] = useState("")

  // Initialize selectedIds when drawer opens with preSelectedIds
  React.useEffect(() => {
    if (open) {
      setSelectedIds(preSelectedIds)
    }
  }, [open, preSelectedIds])

  const params = useParams()
  const x = params.x

  const workplaceIdToUse = workplaceId || (x ? Number(x) : undefined)
  const { data: workplaceEmployees, isLoading: isLoadingWorkplaceEmployees } =
    useGet<IEmployeeAssignmentRes>({
      url: `/v1/time-attendance/workplaces/${
        workplaceId || x
      }/employees?limit=100`,
      instanceKey: "ewaAxiosInstance",
      enabled: !!workplaceIdToUse,
    })

  const workplaceEmployeesData = workplaceEmployees?.data?.docs ?? []

  const departments: Department[] = React.useMemo(() => {
    const map = new Map<string, Department>()

    workplaceEmployeesData.forEach((emp) => {
      if (!emp) return
      const deptId = String(emp.department?.id || 0)
      const deptName = emp.department?.name || "Unknown Department"
      const id = String(emp.id)
      const rawName = `${emp.firstName || ""} ${emp.lastName || ""}`.trim()
      const initials = getInitials(rawName)

      if (!rawName) return // Skip employees without names

      if (!map.has(deptId)) {
        map.set(deptId, {
          id: deptId,
          name: deptName,
          staff: [],
        })
      }

      map.get(deptId)!.staff.push({
        id,
        name: rawName,
        initials,
      })
    })

    return Array.from(map.values())
  }, [workplaceEmployeesData])

  const allStaffIds = departments.flatMap((d) => d.staff.map((s) => s.id))
  const allSelected =
    allStaffIds.length > 0 &&
    allStaffIds.every((id) => selectedIds.includes(id))

  const toggleStaff = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    )
  }

  const toggleDepartment = (dept: Department, checked: boolean) => {
    const deptIds = dept.staff.map((s) => s.id)
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...deptIds])])
    } else {
      setSelectedIds((prev) => prev.filter((id) => !deptIds.includes(id)))
    }
  }

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? allStaffIds : [])
  }

  const isDeptAllSelected = (dept: Department) =>
    dept.staff.every((s) => selectedIds.includes(s.id))

  const isDeptPartial = (dept: Department) =>
    dept.staff.some((s) => selectedIds.includes(s.id)) &&
    !isDeptAllSelected(dept)

  const filteredDepartments = departments
    .map((dept) => ({
      ...dept,
      staff: dept.staff.filter((s) =>
        s.name.toLowerCase().includes(search.toLowerCase())
      ),
    }))
    .filter((dept) => dept.staff.length > 0)

  const idToStaff = React.useMemo(
    () =>
      new Map(
        departments.flatMap((d) => d.staff.map((s) => [s.id, s] as const))
      ),
    [departments]
  )

  const handleSave = () => {
    if (saveLoading) return
    const selected = selectedIds.map((id) => ({
      id,
      name: idToStaff.get(id)?.name ?? `Employee ${id}`,
    }))
    onSave(selected)
    // Parent closes drawer after save request completes
  }

  const handleClose = () => {
    setSelectedIds([])
    setSearch("")
    form.resetFields()
    onClose()
  }

  return (
    <Drawer
      rootClassName="ta-scope"
      open={open}
      onClose={handleClose}
      title={null}
      closable={false}
      width={480}
      footer={
        <div className="flex justify-end">
          <Button
            type="primary"
            className="font-semibold px-6 h-10 rounded-lg bg-[#004AF5] text-white"
            onClick={handleSave}
            loading={saveLoading}
            disabled={saveLoading}
          >
            Save selection
          </Button>
        </div>
      }
      styles={{
        body: { padding: 0 },
        footer: { padding: "16px", borderTop: "none" },
      }}
    >
      <Form
        form={form}
        layout="vertical"
        className="h-full flex flex-col"
      >
        {/* header */}
        <div className="flex items-start justify-between px-4 pt-4 pb-4">
          <div className="flex flex-col gap-1">
            <h3 className="font-bold text-lg text-[#0F172B]">
              Select staff for this shift slot
            </h3>
            <p className="text-sm text-[#64748B]">
              Select employees to assign to this shift plan.
            </p>
          </div>
          <button
            className="p-1 hover:bg-[#F1F5F9] rounded text-[#64748B] text-xl leading-none"
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        <div className="px-4 pb-0 border-b border-[#E2E8F0]">
          <Form.Item
            name="search"
            noStyle
          >
            <Input
              placeholder="Search for a staff"
              prefix={
                <MagnifyingGlassIcon className="w-4 h-4 text-[#94A3B8]" />
              }
              className="h-10 !rounded-none !border-0 !shadow-none !outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Form.Item>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0]">
            <Checkbox
              checked={allSelected}
              onChange={(e) => toggleAll(e.target.checked)}
            />
            <span className="text-sm text-[#0F172B]">Select all</span>
          </div>

          {filteredDepartments.map((dept) => (
            <div key={dept.id}>
              <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E2E8F0]">
                <Checkbox
                  checked={isDeptAllSelected(dept)}
                  onChange={(e) => toggleDepartment(dept, e.target.checked)}
                />
                <span className="font-bold text-sm text-[#0F172B]">
                  {dept.name}
                </span>
              </div>

              {dept.staff.map((staff) => (
                <div
                  key={staff.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <Checkbox
                    checked={selectedIds.includes(staff.id)}
                    onChange={() => toggleStaff(staff.id)}
                  />
                  <span className="rounded-full bg-[#E2E8F0] w-8 h-8 flex items-center justify-center font-semibold text-xs text-[#0F172B] ml-2">
                    {staff.initials}
                  </span>
                  <span className="text-sm text-[#0F172B]">{staff.name}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#E2E8F0] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {isLoadingWorkplaceEmployees && (
              <span className="inline-flex items-center gap-1 text-xs text-[#64748B]">
                <Spin size="small" />
                <span>Loading staff...</span>
              </span>
            )}
          </div>
        </div>
      </Form>
    </Drawer>
  )
}

export default StaffSelectionDrawer
